/**
 * Environment-aware Logger
 * 
 * Dev-only:  info, debug, warn, api, performance, auth, apiRequest, apiResponse
 * Always-on: error, apiError — these always log so they appear in DO runtime logs
 * 
 * All production logs are prefixed with [DASHBOARD] for easy filtering.
 * Sensitive data (tokens, passwords, PII) must NEVER be passed to these loggers.
 */

import config, { isDevelopment } from '../config/environment';

const LOG_LEVELS = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
};

const getCurrentLogLevel = () => {
  return LOG_LEVELS[config.logging.level] || LOG_LEVELS.info;
};

const shouldLog = (level) => {
  // Only log in development environment
  if (!isDevelopment()) return false;
  if (!config.logging.enabled) return false;
  return LOG_LEVELS[level] <= getCurrentLogLevel();
};

const formatMessage = (level, message, data) => {
  const timestamp = new Date().toISOString();
  const env = config.env.toUpperCase();
  return {
    timestamp,
    env,
    level: level.toUpperCase(),
    message,
    ...(data && { data }),
  };
};

export const logger = {
  // ─── ALWAYS-ON (production-safe) ──────────────────────────────────
  // These always log so errors are visible in DO Runtime Logs.
  // Do NOT pass sensitive data (tokens, passwords, PII) to these methods.

  error: (message, data) => {
    if (isDevelopment()) {
      console.error('❌', formatMessage('error', message, data));
    } else {
      // Production: structured one-liner for DO runtime logs
      console.error(`[DASHBOARD ERROR] ${message}`, data ? JSON.stringify(data) : '');
    }
  },

  apiError: (message, data) => {
    if (isDevelopment()) {
      console.error('🚨', formatMessage('api-error', message, data));
    } else {
      // Production: always log API errors — these are critical for debugging
      // Strip sensitive headers before logging
      const safeData = data ? { ...data } : {};
      if (safeData.headers) delete safeData.headers;
      if (safeData.body) delete safeData.body;
      console.error(`[DASHBOARD API ERROR] ${message}`, JSON.stringify(safeData));
    }
  },

  // ─── Development-only loggers ─────────────────────────────────────

  warn: (message, data) => {
    if (shouldLog('warn')) {
      console.warn('⚠️', formatMessage('warn', message, data));
    }
  },

  info: (message, data) => {
    if (shouldLog('info')) {
      console.info('ℹ️', formatMessage('info', message, data));
    }
  },

  debug: (message, data) => {
    if (shouldLog('debug')) {
      console.log('🐛', formatMessage('debug', message, data));
    }
  },

  api: (method, url, data) => {
    if (isDevelopment() && config.logging.apiCalls) {
      console.log('🌐', formatMessage('api', `${method} ${url}`, data));
    }
  },

  performance: (label, duration) => {
    if (isDevelopment() && config.logging.performance) {
      console.log('⚡', formatMessage('performance', `${label}: ${duration}ms`));
    }
  },

  auth: (message, data) => {
    if (shouldLog('info')) {
      console.log('🔐', formatMessage('auth', message, data));
    }
  },

  apiRequest: (message, data) => {
    if (isDevelopment() && config.logging.apiCalls && shouldLog('debug')) {
      console.log('📤', formatMessage('api-request', message, data));
    }
  },

  apiResponse: (message, data) => {
    if (isDevelopment() && config.logging.apiCalls && shouldLog('debug')) {
      console.log('📥', formatMessage('api-response', message, data));
    }
  },
};

export default logger;
